import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import {
  Home as HomeIcon, // eslint-disable-line no-unused-vars
  Search,
  Download,
  ShieldCheck,
  Sparkles,
  Ruler,
  X,
  CheckCircle2,
  Compass,
  ChevronRight,
  Filter,
  BedDouble,
  Eye,
  Box,
  Loader2,
  Pencil,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import LanguageToggle from "@/components/LanguageToggle";
import LegalDisclaimer from "@/components/LegalDisclaimer";
import BrandLogo from "@/components/BrandLogo";
import Phase1Scope from "@/components/Phase1Scope";
import AddonsSection from "@/components/AddonsSection";
import { BudgetEstimateCard } from "@/components/BudgetEstimateCard";
import { CheckoutModal } from "@/components/checkout/CheckoutModal";
import { t } from "@/i18n";
import { useAuth } from "@/auth/AuthContext";
import { trackEvent } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA_PHONE = "919717667115";
// Render the entire library in a single fetch. Backend caps page_size at 60,
// and the library is well under that (40-55 designs), so we deliberately
// avoid pagination/infinite-scroll edge cases that can hide cards. The
// IntersectionObserver-based scroll loader still works as a safety net if
// the library ever grows past 60.
const PAGE_SIZE = 60;
const formatINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

// Lightweight hook for the public payment-settings doc. Drives both the
// strikethrough/discount UI and the "payments paused" disabled state.
const usePaymentSettings = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    let cancel = false;
    const load = () => {
      axios
        .get(`${API}/payment-settings`)
        .then(({ data }) => {
          if (!cancel) setS(data);
        })
        .catch((err) => {
          // Non-fatal — UI degrades to "payments enabled with no discount"
          // when payment-settings is unreachable. Log so we still notice.
          console.warn("payment-settings fetch failed:", err?.message || err);
        });
    };
    load();
    const tick = setInterval(load, 60000);
    return () => {
      cancel = true;
      clearInterval(tick);
    };
  }, []);
  return s;
};

// Pure helper — given an original price + settings object, return the
// effective price applying the flat override (if any) and the active discount.
const applyDiscount = (price, settings) => {
  if (!settings) return Number(price || 0);
  const base = Number(settings.standard_price_override || 0) || Number(price || 0);
  if (!settings.discount_active || !settings.discount_percent) {
    return base;
  }
  const pct = Math.max(0, Math.min(100, Number(settings.discount_percent)));
  return Math.max(0, Math.round((base * (100 - pct)) / 100));
};

// Plot area buckets — translates to (min_area, max_area) backend params.
const AREA_BUCKETS = [
  { id: "all", labelKey: "bz_area_any", min: null, max: null },
  { id: "<500", labelKey: "bz_area_xs", min: 0, max: 499 },
  { id: "500-1000", labelKey: "bz_area_s", min: 500, max: 1000 },
  { id: "1000-1500", labelKey: "bz_area_m", min: 1000, max: 1500 },
  { id: "1500-2500", labelKey: "bz_area_l", min: 1500, max: 2500 },
  { id: ">2500", labelKey: "bz_area_xl", min: 2500, max: null },
];
const FACING_OPTIONS = ["any", "north", "south", "east", "west"];
const BHK_OPTIONS = [0, 1, 2, 3, 4]; // 0 = any

const useDebounce = (value, ms = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
};

// Admin mode flag — set by the AdminDashboard on mount. Drives the
// "Review / Edit" mode in the storefront: bypasses the payment gate, hides
// promo/paused banners, removes 2D-preview watermarks, and rewires Buy CTAs
// into direct asset-open buttons.
const useAdminMode = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    try {
      // Also support ?admin=1 / ?exit_admin=1 deep-links.
      const url = new URL(window.location.href);
      if (url.searchParams.get("admin") === "1") {
        window.sessionStorage.setItem("mn_admin", "1");
      }
      if (url.searchParams.get("exit_admin") === "1") {
        window.sessionStorage.removeItem("mn_admin");
      }
      setIsAdmin(window.sessionStorage.getItem("mn_admin") === "1");
    } catch {
      setIsAdmin(false);
    }
  }, []);
  return [isAdmin, setIsAdmin];
};

const NakshaStore = () => {
  const [lang, setLang] = useState("hi");
  const paymentSettings = usePaymentSettings();
  const [isAdmin, setIsAdmin] = useAdminMode();
  const { customer } = useAuth();

  // When a customer is signed in, pull their verified purchases so we can
  // flag designs they already own ("Already purchased") in the grid.
  const [ownedSet, setOwnedSet] = useState(() => new Set());
  useEffect(() => {
    if (!customer) {
      setOwnedSet(new Set());
      return;
    }
    let cancel = false;
    axios
      .get(`${API}/customer/purchases`)
      .then(({ data }) => {
        if (cancel) return;
        const ids = new Set(
          (data.items || [])
            .filter((p) => p.payment_status === "verified")
            .map((p) => p.design_id)
        );
        setOwnedSet(ids);
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, [customer]);

  // Keep <html lang> in sync so the global PromoBanner can pick the right
  // language without prop-drilling.
  useEffect(() => {
    document.documentElement.lang = lang;
    // Fire library-viewed once on mount. `total_designs_shown` is still
    // unknown at this point; the second effect below re-fires it with a
    // richer payload once the first fetch resolves.
    trackEvent("design_library_viewed", {
      category_filter: "all",
      total_designs_shown: 0,
    });
  }, [lang]);
  const [filters, setFilters] = useState({
    area: "all",
    facing: "any",
    bhk: 0,
    collection: "",  // "" | "bihar"
  });
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 350);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false); // mobile sidebar
  const [luxuryItems, setLuxuryItems] = useState([]); // ★ Luxury Collection rail (≥1500 sqft)

  const [purchase, setPurchase] = useState(null); // design being checked out
  const [receipt, setReceipt] = useState(null);
  const [previewDesign, setPreviewDesign] = useState(null); // for 2D plan preview
  const [renderDesign, setRenderDesign] = useState(null); // for full 3D image

  const requestId = useRef(0);
  const sentinelRef = useRef(null);

  // Design viewed — fire whenever the user opens the 3D-render modal
  // (the primary "design detail" viewport in the store).
  useEffect(() => {
    if (!renderDesign) return;
    trackEvent("design_viewed", {
      design_id: renderDesign.id,
      design_name: renderDesign.name,
      design_category: renderDesign.collection || renderDesign.badge || "general",
      plot_size: renderDesign.size_label,
    });
  }, [renderDesign]);

  // Deep-link support: /store?buy=<id> auto-opens checkout, /store?focus=<id> scrolls
  const [searchParams, setSearchParams] = useSearchParams();
  const buyDeepLinkId = searchParams.get("buy");
  const focusDeepLinkId = searchParams.get("focus");
  const deepLinkHandled = useRef(false);

  useEffect(() => {
    if (deepLinkHandled.current) return;
    const target = buyDeepLinkId || focusDeepLinkId;
    if (!target) return;
    deepLinkHandled.current = true;
    axios
      .get(`${API}/library/${encodeURIComponent(target)}`)
      .then(({ data }) => {
        if (buyDeepLinkId) {
          setReceipt(null);
          setPurchase(data);
        } else if (focusDeepLinkId) {
          setRenderDesign(data);
        }
      })
      .catch(() => {})
      .finally(() => {
        // Clear the param so refresh doesn't reopen
        const next = new URLSearchParams(searchParams);
        next.delete("buy");
        next.delete("focus");
        setSearchParams(next, { replace: true });
      });
  }, [buyDeepLinkId, focusDeepLinkId, searchParams, setSearchParams]);

  // Track first-paint latency once per session — feeds the Admin System
  // Health widget so the founder can sense Tier-3/4 connectivity.
  useEffect(() => {
    try {
      if (window.sessionStorage.getItem("mn_lat") === "1") return;
      const nav = performance.getEntriesByType("navigation")[0];
      const ms = nav ? Math.round(nav.domContentLoadedEventEnd) : null;
      if (!ms) return;
      window.sessionStorage.setItem("mn_lat", "1");
      axios
        .post(`${API}/admin/track-latency`, { ms, ua: navigator.userAgent })
        .catch(() => {});
    } catch (err) {
      // Private/Incognito mode throws when accessing sessionStorage —
      // non-critical; surface only in dev so we don't pollute prod consoles.
      if (process.env.NODE_ENV !== "production") {
        console.warn("latency tracking skipped:", err?.message || err);
      }
    }
  }, []);

  // One-time fetch of the Luxury Collection (≥1500 sqft, premium designs).
  // Rendered as a horizontally-scrolling rail at the top of the bazaar so big
  // plot owners spot the right options instantly. Only shown when no filters
  // are active, to avoid duplicating cards in the main grid.
  useEffect(() => {
    let cancel = false;
    axios
      .get(`${API}/library?min_area=1500&page=1&page_size=20`)
      .then(({ data }) => {
        if (cancel) return;
        setLuxuryItems(data.items || []);
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, []);

  // Build query params from filters/search/page
  const buildParams = useCallback(
    (pg) => {
      const p = new URLSearchParams();
      const bucket = AREA_BUCKETS.find((b) => b.id === filters.area);
      if (bucket && bucket.min !== null) p.set("min_area", String(bucket.min));
      if (bucket && bucket.max !== null) p.set("max_area", String(bucket.max));
      if (filters.facing && filters.facing !== "any") p.set("facing", filters.facing);
      if (filters.bhk) p.set("bhk", String(filters.bhk));
      if (filters.collection) p.set("collection", filters.collection);
      if (debouncedSearch.trim()) p.set("q", debouncedSearch.trim());
      p.set("page", String(pg));
      p.set("page_size", String(PAGE_SIZE));
      return p.toString();
    },
    [filters, debouncedSearch]
  );

  // Reset to page 1 whenever filters or search changes
  useEffect(() => {
    requestId.current += 1;
    const myId = requestId.current;
    setLoading(true);
    setPage(1);
    axios
      .get(`${API}/library?${buildParams(1)}`)
      .then(({ data }) => {
        if (requestId.current !== myId) return;
        setItems(data.items);
        setTotal(data.total);
        if (debouncedSearch.trim()) {
          // Search actually used — fire once per resolved query so we
          // capture the pair (query, results_count) rather than every
          // keystroke.
          trackEvent("search_used", {
            search_query: debouncedSearch.trim(),
            results_count: data.total,
          });
        }
      })
      .catch(() => {
        if (requestId.current !== myId) return;
        setItems([]);
        setTotal(0);
      })
      .finally(() => requestId.current === myId && setLoading(false));
  }, [buildParams]);

  // Infinite scroll
  const hasMore = items.length < total;
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore || loading) return;
    const next = page + 1;
    setLoadingMore(true);
    axios
      .get(`${API}/library?${buildParams(next)}`)
      .then(({ data }) => {
        setItems((prev) => [...prev, ...data.items]);
        setTotal(data.total);
        setPage(next);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }, [page, loading, loadingMore, hasMore, buildParams]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "320px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore]);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => {
    setFilters({ area: "all", facing: "any", bhk: 0, collection: "" });
    setSearch("");
  };
  const activeCount =
    (filters.area !== "all" ? 1 : 0) +
    (filters.facing !== "any" ? 1 : 0) +
    (filters.bhk ? 1 : 0) +
    (filters.collection ? 1 : 0);

  return (
    <div className="min-h-screen bg-sand" data-testid="store-root">
      {/* Admin Mode strip — shown above the standard header when the founder
          jumped from /admin into the store. Carries an "Exit" button so they
          can drop back into the regular customer view at any time. */}
      {isAdmin && (
        <div
          className="bg-navy text-gold border-b-2 border-gold/40 text-[12.5px]"
          data-testid="store-admin-mode-bar"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-2 flex items-center justify-between gap-3">
            <div className="inline-flex items-center gap-2 font-extrabold tracking-wide">
              <ShieldCheck className="h-4 w-4" />
              ADMIN MODE
              <span className="hidden sm:inline text-gold/70 font-semibold normal-case tracking-normal">
                · payment gate bypassed · all 2D / 3D files openable
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/admin"
                data-testid="store-back-to-admin"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 font-bold"
              >
                ← Admin
              </Link>
              <button
                onClick={() => {
                  try {
                    window.sessionStorage.removeItem("mn_admin");
                  } catch (err) {
                    // Private mode — sessionStorage unavailable. Safe to ignore.
                    if (process.env.NODE_ENV !== "production") {
                      console.warn("admin flag clear failed:", err?.message || err);
                    }
                  }
                  setIsAdmin(false);
                }}
                data-testid="store-exit-admin"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-gold text-navy hover:bg-gold-dark hover:text-white font-extrabold"
              >
                Exit Admin
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-30 bg-navy text-white border-b-4 border-gold shadow-[0_8px_24px_-12px_rgba(11,37,69,0.4)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/"
              data-testid="store-home-link"
              aria-label="Back to home"
              className="inline-flex items-center bg-white rounded-lg px-2 py-1 shadow-sm shrink-0"
            >
              <BrandLogo size="sm" asLink={false} withTagline lang={lang} testId="store-brand-logo" />
            </Link>
            <div className="leading-tight min-w-0">
              <div className="text-[11px] uppercase tracking-[0.2em] text-gold font-bold truncate">
                {t(lang, "bz_kicker")}
              </div>
              <div className="text-[15px] font-extrabold tracking-tight truncate">
                {t(lang, "bz_title")}
              </div>
            </div>
          </div>
          <LanguageToggle lang={lang} onChange={setLang} />
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-5 sm:py-7">
        {/* Hero strip */}
        <section
          className="relative overflow-hidden rounded-3xl bg-navy-jaali text-white p-5 sm:p-7 shadow-[0_30px_60px_-30px_rgba(11,37,69,0.55)]"
          data-testid="store-hero"
        >
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-gold-dark via-gold to-gold-dark" />
          <div className="absolute -right-10 -bottom-10 w-56 h-56 rounded-full bg-gold/10 blur-2xl pointer-events-none" />
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/15 border border-gold/30 text-gold text-[11px] font-bold tracking-wider uppercase">
            <Sparkles className="h-3.5 w-3.5" /> {t(lang, "bz_eyebrow")}
          </div>
          <h1 className="mt-3 text-[26px] sm:text-4xl font-extrabold leading-[1.08] tracking-tight text-balance">
            {t(lang, "bz_h1_a")}{" "}
            <span className="gold-shimmer animate-shimmer">{t(lang, "bz_h1_b")}</span>
          </h1>
          <p className="mt-2 text-[14.5px] sm:text-base text-white/75 leading-relaxed max-w-prose">
            {t(lang, "bz_sub")}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {["bz_perk_1", "bz_perk_2", "bz_perk_3"].map((k) => (
              <span
                key={k}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-[12px] font-semibold"
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-gold" />
                {t(lang, k)}
              </span>
            ))}
          </div>
        </section>

        {/* Search + mobile filter trigger */}
        <div className="mt-5 flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 rounded-2xl bg-white border-2 border-border px-3 py-2 focus-within:border-navy">
            <Search className="h-5 w-5 text-navy" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t(lang, "bz_search_ph")}
              data-testid="bz-search-input"
              className="flex-1 bg-transparent text-[14px] outline-none placeholder:text-muted-foreground"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                aria-label="Clear"
                className="h-7 w-7 grid place-items-center rounded-full hover:bg-sand-alt"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
          </div>
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="bz-mobile-filter-btn"
            className="lg:hidden inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-2xl border-2 border-navy bg-white text-navy text-sm font-bold shrink-0"
          >
            <Filter className="h-4 w-4" />
            {t(lang, "bz_filters")}
            {activeCount > 0 && (
              <span className="h-5 min-w-[20px] px-1 rounded-full bg-gold text-navy text-[11px] font-extrabold grid place-items-center">
                {activeCount}
              </span>
            )}
          </button>
        </div>

        {/* Result summary */}
        <div className="mt-3 text-[12.5px] text-muted-foreground" data-testid="bz-result-summary">
          {loading
            ? t(lang, "bz_loading")
            : `${total} ${t(lang, "bz_designs_found")}`}
          {activeCount > 0 && !loading && (
            <button
              onClick={clearFilters}
              data-testid="bz-clear-filters"
              className="ml-2 text-navy font-semibold underline underline-offset-2"
            >
              {t(lang, "bz_clear_all")}
            </button>
          )}
        </div>

        <div className="mt-3 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
          {/* Sidebar (desktop) */}
          <aside
            className="hidden lg:block self-start sticky top-20"
            data-testid="bz-sidebar-desktop"
          >
            <FilterPanel
              lang={lang}
              filters={filters}
              setFilter={setFilter}
              clear={clearFilters}
              activeCount={activeCount}
            />
          </aside>

          {/* Grid */}
          <main>
            {/* ★ Luxury Collection rail — featured wide-plot designs (≥1500 sqft).
                Only shown on the default view (no filters/search) so it doesn't
                duplicate cards in filtered results. */}
            {!loading && activeCount === 0 && !debouncedSearch.trim() && luxuryItems.length > 0 && (
              <LuxuryRail
                lang={lang}
                items={luxuryItems}
                settings={paymentSettings}
                isAdmin={isAdmin}
                onView3D={(d) => setRenderDesign(d)}
                onBuy={(d) => {
                  setReceipt(null);
                  setPurchase(d);
                }}
              />
            )}
            {loading ? (
              <SkeletonGrid />
            ) : items.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground" data-testid="bz-empty">
                <Box className="h-10 w-10 mx-auto text-navy/30" />
                <div className="mt-3 font-bold text-navy">{t(lang, "bz_empty_t")}</div>
                <div className="text-sm">{t(lang, "bz_empty_d")}</div>
              </div>
            ) : (
              <section
                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5"
                data-testid="bz-grid"
              >
                {items.map((d) => (
                  <DesignCard
                    key={d.id}
                    design={d}
                    lang={lang}
                    settings={paymentSettings}
                    isAdmin={isAdmin}
                    owned={ownedSet.has(d.id)}
                    onPreview={() => setPreviewDesign(d)}
                    onView3D={() => setRenderDesign(d)}
                    onBuy={() => {
                      setReceipt(null);
                      setPurchase(d);
                    }}
                  />
                ))}
              </section>
            )}

            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className="h-10" />
            {loadingMore && (
              <div className="text-center text-muted-foreground py-2 flex items-center justify-center gap-2" data-testid="bz-loading-more">
                <Loader2 className="h-4 w-4 animate-spin" /> {t(lang, "bz_loading_more")}
              </div>
            )}
            {!loading && !hasMore && items.length > 0 && (
              <div className="text-center text-muted-foreground text-[12px] py-4" data-testid="bz-end-of-list">
                — {t(lang, "bz_end")} —
              </div>
            )}
          </main>
        </div>

        {/* Trust strip */}
        <section className="mt-8 rounded-2xl bg-white border border-border p-4 flex items-start sm:items-center gap-3 shadow-sm">
          <div className="h-10 w-10 rounded-xl bg-navy text-gold grid place-items-center shrink-0">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="text-[13px] text-foreground">
            <div className="font-bold text-navy">{t(lang, "bz_trust_t")}</div>
            <div className="text-muted-foreground">{t(lang, "bz_trust_d")}</div>
          </div>
        </section>
      </div>

      {/* Mobile filter drawer */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm lg:hidden flex items-end"
          onClick={() => setDrawerOpen(false)}
          data-testid="bz-drawer-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full bg-white rounded-t-3xl border-t-2 border-gold max-h-[85vh] overflow-y-auto p-5 animate-fade-up"
            data-testid="bz-drawer"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="font-extrabold text-navy text-lg flex items-center gap-2">
                <Filter className="h-5 w-5" /> {t(lang, "bz_filters")}
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                aria-label="Close"
                className="h-9 w-9 grid place-items-center rounded-full bg-sand-alt"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <FilterPanel
              lang={lang}
              filters={filters}
              setFilter={setFilter}
              clear={clearFilters}
              activeCount={activeCount}
            />
            <button
              onClick={() => setDrawerOpen(false)}
              data-testid="bz-drawer-apply"
              className="mt-4 w-full min-h-[52px] rounded-2xl bg-navy text-gold font-extrabold text-[14.5px]"
            >
              {t(lang, "bz_show_results")} ({total})
            </button>
          </div>
        </div>
      )}

      {/* 3D render lightbox */}
      {renderDesign && (
        <ImageLightbox
          design={renderDesign}
          lang={lang}
          settings={paymentSettings}
          onClose={() => setRenderDesign(null)}
          onBuy={() => {
            const d = renderDesign;
            setRenderDesign(null);
            setReceipt(null);
            setPurchase(d);
          }}
        />
      )}

      {/* 2D plan preview (watermarked) */}
      {previewDesign && (
        <PlanPreview
          design={previewDesign}
          lang={lang}
          isAdmin={isAdmin}
          owned={ownedSet.has(previewDesign.id)}
          settings={paymentSettings}
          onClose={() => setPreviewDesign(null)}
          onBuy={() => {
            const d = previewDesign;
            setPreviewDesign(null);
            setReceipt(null);
            setPurchase(d);
          }}
        />
      )}

      {/* Checkout / receipt */}
      {purchase && (
        <CheckoutModal
          design={purchase}
          lang={lang}
          settings={paymentSettings}
          onClose={() => {
            setPurchase(null);
            setReceipt(null);
          }}
          onSuccess={(r) => setReceipt(r)}
          receipt={receipt}
        />
      )}
    </div>
  );
};

// ★ Luxury Collection horizontal rail
const LuxuryRail = ({ lang, items, settings, isAdmin, onView3D, onBuy }) => {
  const paused = !isAdmin && settings && settings.payments_enabled === false;
  const hasDiscount = !isAdmin && settings && settings.discount_active && settings.discount_percent > 0;
  return (
  <section
    className="mb-5 rounded-3xl bg-gradient-to-br from-navy via-navy-deep to-navy text-white p-4 sm:p-5 shadow-[0_24px_48px_-24px_rgba(11,37,69,0.6)] border-2 border-gold/40 relative overflow-hidden"
    data-testid="luxury-collection-rail"
  >
    <div className="absolute -right-12 -top-12 w-44 h-44 rounded-full bg-gold/10 blur-2xl pointer-events-none" />
    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold-dark via-gold to-gold-dark" />
    <div className="flex items-end justify-between gap-3 mb-3 relative">
      <div>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gold/15 border border-gold/30 text-gold text-[10.5px] font-bold tracking-widest uppercase">
          <Sparkles className="h-3 w-3" /> {lang === "hi" ? "विशेष संग्रह" : "Premium Picks"}
        </div>
        <h2 className="mt-1.5 text-[20px] sm:text-2xl font-extrabold tracking-tight">
          {lang === "hi" ? "लग्ज़री कलेक्शन " : "Luxury Collection "}
          <span className="text-gold">·</span>
          <span className="text-gold/90 text-[14px] sm:text-base font-bold ml-1">
            1500+ sqft · Wide Plot
          </span>
        </h2>
      </div>
      <span className="hidden sm:inline-flex shrink-0 px-2.5 py-1 rounded-full bg-white/10 text-[11px] font-bold text-white/80">
        {items.length} {lang === "hi" ? "नक्शे" : "designs"}
      </span>
    </div>
    <div
      className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 -mx-4 sm:-mx-5 px-4 sm:px-5 snap-x snap-mandatory no-scrollbar"
      data-testid="luxury-rail-scroller"
    >
      {items.map((d) => (
        <article
          key={d.id}
          className="snap-start shrink-0 w-[230px] sm:w-[260px] rounded-2xl bg-white text-foreground border border-gold/30 overflow-hidden shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all flex flex-col"
          data-testid={`luxury-card-${d.id}`}
        >
          <button
            onClick={() => onView3D(d)}
            className="relative aspect-[4/3] bg-sand-alt overflow-hidden text-left group/luxe"
            aria-label={`Preview ${d.name}`}
          >
            <img
              src={d.image_url}
              alt={`${d.name} 3D render`}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover/luxe:scale-[1.04]"
            />
            <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-gold-dark to-gold text-navy text-[10.5px] font-extrabold uppercase tracking-wider shadow-md border border-gold-dark">
              <Sparkles className="h-2.5 w-2.5" /> Luxury
            </span>
            <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-navy/90 text-gold text-[10.5px] font-extrabold tracking-wider">
              {d.size_label}
            </span>
          </button>
          <div className="p-3 flex flex-col gap-2 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="text-[14.5px] font-extrabold tracking-tight text-navy truncate">
                  {d.name}
                </h3>
                <div className="text-[11.5px] text-muted-foreground flex items-center gap-1.5">
                  <Ruler className="h-3 w-3" /> {d.area_sqft} {t(lang, "sqft")}
                  <span>·</span>
                  <BedDouble className="h-3 w-3" /> {d.bhk} BHK
                </div>
              </div>
              <div className="text-right shrink-0">
                {(() => {
                  const dPrice = applyDiscount(d.price, settings);
                  const showStrike = hasDiscount && dPrice < d.price;
                  // When override is on, the "regular" catalog price is
                  // meaningless to the customer — just show the flat fee.
                  return showStrike ? (
                    <>
                      <div className="text-[11px] text-muted-foreground line-through tabular-nums leading-tight">
                        {formatINR(d.price)}
                      </div>
                      <div className="text-[14px] font-extrabold text-gold-dark tabular-nums leading-tight">
                        {formatINR(dPrice)}
                      </div>
                    </>
                  ) : (
                    <div className="text-[14px] font-extrabold text-gold-dark tabular-nums">
                      {formatINR(dPrice)}
                    </div>
                  );
                })()}
              </div>
            </div>
            <button
              onClick={() => {
                if (isAdmin) {
                  const firstPlan =
                    (d.plan_files && d.plan_files[0] && d.plan_files[0].url) || d.plan_url;
                  if (firstPlan) window.open(firstPlan, "_blank", "noopener");
                } else {
                  onBuy(d);
                }
              }}
              disabled={paused}
              data-testid={`luxury-buy-${d.id}`}
              className={`mt-auto min-h-[40px] rounded-xl font-extrabold text-[12.5px] flex items-center justify-center gap-1.5 transition-all ${
                paused
                  ? "bg-sand-alt text-muted-foreground cursor-not-allowed"
                  : "bg-navy text-gold hover:bg-navy-deep active:scale-[0.99]"
              }`}
            >
              <Download className="h-3.5 w-3.5" />
              {paused
                ? lang === "hi"
                  ? "पेमेंट बंद"
                  : "Paused"
                : isAdmin
                ? lang === "hi"
                  ? "रिव्यू / एडिट"
                  : "Review / Edit"
                : t(lang, "store_buy_cta")}
            </button>
            {isAdmin && (
              <a
                href={d.image_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`luxury-admin-3d-${d.id}`}
                className="min-h-[34px] mt-1 rounded-lg border border-navy/20 bg-white text-navy font-bold text-[11.5px] flex items-center justify-center gap-1.5 hover:bg-navy hover:text-gold transition-colors"
              >
                <Box className="h-3 w-3" /> Open 3D
              </a>
            )}
          </div>
        </article>
      ))}
    </div>
  </section>
  );
};

const FilterGroup = ({ title, icon, children }) => (
  <div className="rounded-2xl bg-white border border-border p-3.5">
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-navy font-bold">
      {icon}
      {title}
    </div>
    <div className="mt-3 flex flex-col gap-1.5">{children}</div>
  </div>
);

const FilterPanel = ({ lang, filters, setFilter, clear, activeCount }) => {
  return (
    <div className="space-y-3">
      {/* Pan-India trust badge — replaces the earlier region-specific
          "Bihar Special Collection" toggle. Static (non-interactive) so the
          subtitle continues to communicate institutional positioning
          across the whole Bharat market. */}
      <div
        className="w-full rounded-2xl p-3 border-2 border-gold/40 bg-gradient-to-r from-navy via-navy-deep to-navy text-white shadow-md flex items-start gap-2.5"
        data-testid="bz-pan-india-badge"
      >
        <span
          className="h-9 w-9 rounded-xl grid place-items-center shrink-0 bg-gold/15 border border-gold/30 text-gold text-[16px]"
          aria-hidden
        >
          🇮🇳
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-extrabold tracking-tight text-gold">
            {lang === "hi"
              ? "भारत के लिए भरोसेमंद होम डिज़ाइन कॉन्सेप्ट्स"
              : "Trusted Home Design Concepts for Bharat"}
          </div>
          <div className="text-[11px] mt-0.5 text-white/75 leading-snug">
            {lang === "hi"
              ? "पूरे भारत में पेशेवर होम डिज़ाइन को सुलभ बना रहे हैं।"
              : "Making professional home design accessible across India."}
          </div>
        </div>
      </div>

      <FilterGroup
        title={t(lang, "bz_filter_area")}
        icon={<Ruler className="h-3.5 w-3.5 text-gold-dark" />}
      >
        {AREA_BUCKETS.map((b) => (
          <FilterRadio
            key={b.id}
            name="area"
            checked={filters.area === b.id}
            onChange={() => setFilter("area", b.id)}
            testId={`bz-area-${b.id}`}
          >
            {t(lang, b.labelKey)}
          </FilterRadio>
        ))}
      </FilterGroup>

      <FilterGroup
        title={t(lang, "bz_filter_facing")}
        icon={<Compass className="h-3.5 w-3.5 text-gold-dark" />}
      >
        <div className="grid grid-cols-3 gap-1.5">
          {FACING_OPTIONS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter("facing", f)}
              data-testid={`bz-facing-${f}`}
              className={`h-9 rounded-xl text-[12px] font-bold border-2 capitalize transition-all ${
                filters.facing === f
                  ? "bg-navy text-gold border-navy"
                  : "bg-white text-navy border-border hover:border-navy/40"
              }`}
            >
              {f === "any" ? t(lang, "bz_facing_any") : t(lang, `facing_${f}`)}
            </button>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup
        title={t(lang, "bz_filter_bhk")}
        icon={<BedDouble className="h-3.5 w-3.5 text-gold-dark" />}
      >
        <div className="grid grid-cols-5 gap-1.5">
          {BHK_OPTIONS.map((b) => (
            <button
              key={b}
              onClick={() => setFilter("bhk", b)}
              data-testid={`bz-bhk-${b}`}
              className={`h-9 rounded-xl text-[12px] font-bold border-2 transition-all ${
                filters.bhk === b
                  ? "bg-gold text-navy border-gold-dark"
                  : "bg-white text-navy border-border hover:border-navy/40"
              }`}
            >
              {b === 0 ? t(lang, "bz_bhk_any") : `${b}+`}
            </button>
          ))}
        </div>
      </FilterGroup>

      {activeCount > 0 && (
        <button
          onClick={clear}
          data-testid="bz-clear-sidebar"
          className="w-full h-10 rounded-xl border-2 border-border text-navy font-bold text-[12.5px] hover:bg-sand-alt"
        >
          {t(lang, "bz_clear_all")} ({activeCount})
        </button>
      )}
    </div>
  );
};

const FilterRadio = ({ checked, onChange, children, testId }) => (
  <label
    onClick={onChange}
    data-testid={testId}
    className={`cursor-pointer flex items-center gap-2 rounded-lg px-2.5 py-2 text-[13px] transition-all ${
      checked ? "bg-navy/5 text-navy font-bold" : "text-foreground hover:bg-sand-alt"
    }`}
  >
    <span
      className={`h-4 w-4 rounded-full border-2 grid place-items-center shrink-0 ${
        checked ? "border-navy bg-navy" : "border-border bg-white"
      }`}
    >
      {checked && <span className="h-1.5 w-1.5 rounded-full bg-gold" />}
    </span>
    <span className="flex-1">{children}</span>
  </label>
);

const DesignCard = ({ design, lang, settings, isAdmin, owned, onPreview, onView3D, onBuy }) => {
  const facingChip = design.facing && design.facing !== "any";
  const discounted = applyDiscount(design.price, settings);
  const hasOverride = settings && settings.standard_price_override > 0;
  const hasDiscount = !isAdmin && settings && !hasOverride && settings.discount_active && discounted < design.price;
  const paused = !isAdmin && settings && settings.payments_enabled === false;
  return (
    <article
      className="group rounded-3xl bg-white border-2 border-border overflow-hidden shadow-sm hover:shadow-lg hover:border-gold/50 transition-all flex flex-col"
      data-testid={`design-card-${design.id}`}
    >
      <button
        onClick={onView3D}
        data-testid={`design-3d-trigger-${design.id}`}
        className="relative aspect-[4/3] bg-sand-alt overflow-hidden text-left"
        aria-label={`View ${design.name} 3D render`}
      >
        <img
          src={design.image_url}
          alt={`${design.name} 3D render`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
        {design.is_bestseller && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-gold-dark to-gold text-navy text-[11px] font-extrabold uppercase tracking-wider shadow-md border border-gold-dark">
            <Sparkles className="h-3 w-3" /> {t(lang, "bz_bestseller")}
          </span>
        )}
        {!design.is_bestseller && design.badge && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-gold text-navy text-[11px] font-extrabold uppercase tracking-wider shadow-md">
            {design.badge}
          </span>
        )}
        <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-navy/90 text-gold text-[11px] font-extrabold tracking-wider">
          {design.size_label}
        </span>
        {design.plan_files && design.plan_files.length > 1 && (
          <span
            className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500 text-white text-[11px] font-extrabold tracking-wider shadow-md"
            data-testid={`multi-plan-badge-${design.id}`}
          >
            <Sparkles className="h-3 w-3" />
            {design.plan_files.length} plans included
          </span>
        )}
        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-navy text-[11px] font-bold opacity-0 group-hover:opacity-100 transition-opacity">
          <Eye className="h-3.5 w-3.5" /> {t(lang, "bz_card_zoom")}
        </span>
      </button>

      <div className="p-4 sm:p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-[17px] font-extrabold tracking-tight text-navy truncate">
              {design.name}
            </h3>
            <div className="mt-0.5 text-[12.5px] text-muted-foreground flex items-center gap-2 flex-wrap">
              <span
                className="inline-flex items-center gap-1"
                title={t(lang, "card_plot_size")}
              >
                <Ruler className="h-3.5 w-3.5" />
                {design.size_label}
              </span>
              <span
                className="inline-flex items-center gap-1"
                title={t(lang, "card_builtup")}
              >
                <Box className="h-3.5 w-3.5" />
                {design.area_sqft} {t(lang, "sqft")}
              </span>
              <span
                className="inline-flex items-center gap-1"
                title={t(lang, "card_bedrooms")}
              >
                <BedDouble className="h-3.5 w-3.5" />
                {design.bhk} {t(lang, "card_bedrooms")}
              </span>
              {facingChip && (
                <span className="inline-flex items-center gap-1 capitalize">
                  <Compass className="h-3.5 w-3.5" /> {t(lang, `facing_${design.facing}`)}
                </span>
              )}
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-navy/5 text-navy font-bold text-[10.5px] uppercase tracking-wider"
                data-testid={`card-concept-plan-chip-${design.id}`}
              >
                {t(lang, "card_concept_plan")}
              </span>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-bold">
              {t(lang, "store_price")}
            </div>
            {hasDiscount ? (
              <div className="flex items-baseline gap-1.5 justify-end">
                <span className="text-[12px] text-muted-foreground line-through tabular-nums">
                  {formatINR(design.price)}
                </span>
                <span className="text-xl font-extrabold text-gold-dark tabular-nums">
                  {formatINR(discounted)}
                </span>
              </div>
            ) : (
              <div className="text-xl font-extrabold text-gold-dark tabular-nums">
                {formatINR(discounted)}
              </div>
            )}
            {hasDiscount && (
              <div className="mt-0.5 inline-block px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[10.5px] font-extrabold tracking-wider uppercase">
                {settings.discount_percent}% OFF
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-auto">
          <button
            onClick={onPreview}
            data-testid={`preview-2d-btn-${design.id}`}
            className="min-h-[42px] rounded-xl border-2 border-navy bg-white text-navy font-bold text-[12.5px] flex items-center justify-center gap-1.5 hover:bg-navy hover:text-gold transition-colors"
          >
            <Eye className="h-3.5 w-3.5" /> {t(lang, "bz_preview_2d")}
          </button>
          <button
            onClick={onView3D}
            data-testid={`view-3d-btn-${design.id}`}
            className="min-h-[42px] rounded-xl bg-navy text-gold font-bold text-[12.5px] flex items-center justify-center gap-1.5 hover:bg-navy-deep transition-colors"
          >
            <Box className="h-3.5 w-3.5" /> {t(lang, "bz_view_3d")}
          </button>
        </div>

        <div
          className="rounded-xl border border-navy/10 bg-navy/[0.03] px-3 py-2"
          data-testid={`card-package-badge-${design.id}`}
        >
          <div className="text-[10px] uppercase tracking-[0.16em] text-gold-dark font-extrabold">
            {t(lang, "pkg_name")}
          </div>
          <div className="text-[11.5px] text-muted-foreground leading-snug mt-0.5">
            {t(lang, "phase1_inc_1")} · <span className="line-through">{t(lang, "phase1_exc_1")}</span> · <span className="line-through">MEP</span> · <span className="line-through">Structural</span>
          </div>
        </div>

        <button
          onClick={owned ? () => window.location.assign("/dashboard") : onBuy}
          disabled={paused && !owned}
          data-testid={owned ? `owned-btn-${design.id}` : `buy-btn-${design.id}`}
          className={`min-h-[48px] rounded-2xl font-extrabold text-[14px] flex items-center justify-center gap-2 transition-all shadow-[0_8px_18px_-8px_rgba(212,162,42,0.6)] ${
            owned
              ? "bg-emerald-600 text-white hover:bg-emerald-700"
              : paused
              ? "bg-sand-alt text-muted-foreground cursor-not-allowed shadow-none"
              : "bg-gold text-navy hover:bg-gold-dark hover:text-white active:scale-[0.99]"
          }`}
        >
          {owned ? (
            <>
              <CheckCircle2 className="h-4 w-4" />
              {lang === "hi" ? "खरीदा हुआ · डाउनलोड डैशबोर्ड" : "Already purchased · Open Dashboard"}
              <ChevronRight className="h-4 w-4" />
            </>
          ) : (
            <>
              <Download className="h-4 w-4" />
              {paused
                ? lang === "hi"
                  ? "पेमेंट अस्थायी रूप से बंद"
                  : "Payments paused"
                : t(lang, "store_buy_cta")}
              {!paused && <ChevronRight className="h-4 w-4" />}
            </>
          )}
        </button>

        <LegalDisclaimer
          lang={lang}
          variant="inline"
          testId={`legal-disclaimer-card-${design.id}`}
        />

        {isAdmin && (
          <AdminCardActions design={design} />
        )}
      </div>
    </article>
  );
};

// Admin-only review/edit row shown beneath each card. Lets the founder open
// every attached asset (3D render + every 2D plan_files entry) directly from
// the Bazaar grid, no payment gate involved. Lazy-fetches plan URLs from the
// admin-files endpoint so that public list/detail responses can keep them
// stripped (defense-in-depth).
const AdminCardActions = ({ design }) => {
  const [files, setFiles] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancel = false;
    axios
      .get(`${API}/admin/library/${design.id}/files`)
      .then(({ data }) => {
        if (cancel) return;
        const list =
          data.plan_files && data.plan_files.length > 0
            ? data.plan_files
            : data.plan_url
            ? [{ label: "Plan", url: data.plan_url }]
            : [];
        setFiles(list);
      })
      .catch(() => {
        if (!cancel) setErr("Failed to load files");
      });
    return () => {
      cancel = true;
    };
  }, [design.id]);
  const planFiles = files || [];
  return (
    <div
      className="rounded-2xl border-2 border-navy/15 bg-navy/[0.04] p-2.5 space-y-2"
      data-testid={`admin-actions-${design.id}`}
    >
      <div className="flex items-center justify-between gap-2 px-1">
        <div className="text-[10.5px] uppercase tracking-[0.18em] font-extrabold text-navy">
          🛡️ Admin · Review / Edit
        </div>
        <Link
          to={`/admin?edit=${encodeURIComponent(design.id)}#admin-library`}
          data-testid={`admin-edit-link-${design.id}`}
          title="Open in Library Manager to replace files"
          className="inline-flex items-center gap-1 text-[11px] font-extrabold text-navy hover:text-gold-dark"
        >
          <Pencil className="h-3 w-3" /> Edit
        </Link>
      </div>
      <a
        href={design.image_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`admin-open-3d-${design.id}`}
        className="w-full min-h-[34px] rounded-lg bg-white border border-navy/20 text-navy font-bold text-[12px] flex items-center justify-center gap-1.5 hover:bg-navy hover:text-gold transition-colors"
      >
        <Box className="h-3.5 w-3.5" /> Open 3D render
      </a>
      <div className="grid grid-cols-1 gap-1.5">
        {files === null && !err && (
          <div className="min-h-[34px] rounded-lg bg-white/60 border border-dashed border-navy/20 flex items-center justify-center text-[11px] text-muted-foreground italic">
            Loading plan files…
          </div>
        )}
        {err && (
          <div className="min-h-[34px] rounded-lg bg-red-50 border border-red-200 flex items-center justify-center text-[11px] text-red-700 font-bold">
            {err}
          </div>
        )}
        {planFiles.map((f, i) => (
          <a
            key={f.label + i}
            href={f.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`admin-open-pdf-${design.id}-${i}`}
            className="min-h-[34px] rounded-lg bg-navy text-gold font-bold text-[12px] flex items-center justify-center gap-1.5 hover:bg-navy-deep transition-colors"
          >
            <Download className="h-3.5 w-3.5" /> {f.label}
          </a>
        ))}
      </div>
    </div>
  );
};

const SkeletonGrid = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5" data-testid="bz-skeleton">
    {Array.from({ length: 6 }).map((_, i) => (
      // Static skeleton — list never reorders, so index-as-key is safe.
      // eslint-disable-next-line react/no-array-index-key
      <div key={`bz-skel-${i}`} className="rounded-3xl bg-white border-2 border-border overflow-hidden">
        <div className="aspect-[4/3] bg-sand-alt animate-pulse" />
        <div className="p-4 space-y-2">
          <div className="h-4 bg-sand-alt rounded w-3/5 animate-pulse" />
          <div className="h-3 bg-sand-alt rounded w-2/5 animate-pulse" />
          <div className="h-10 bg-sand-alt rounded mt-3 animate-pulse" />
        </div>
      </div>
    ))}
  </div>
);

const Modal = ({ children, onClose, testId }) => (
  <div
    onClick={onClose}
    data-testid={testId}
    className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 animate-fade-up overflow-y-auto"
  >
    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl my-4 sm:my-8">
      {children}
    </div>
  </div>
);

const ImageLightbox = ({ design, lang, settings, onClose, onBuy }) => (
  <Modal onClose={onClose} testId="bz-3d-lightbox">
    <div className="rounded-3xl bg-white border-2 border-gold overflow-hidden shadow-2xl">
      <div className="relative bg-navy">
        <img
          src={design.image_url}
          alt={`${design.name} 3D render`}
          className="w-full max-h-[70vh] object-contain bg-navy"
        />
        <button
          onClick={onClose}
          data-testid="bz-3d-close"
          className="absolute top-3 right-3 h-10 w-10 grid place-items-center rounded-full bg-white/15 hover:bg-white/30 text-white"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-gold text-navy text-[11px] font-extrabold uppercase tracking-wider shadow-md">
          3D Render
        </div>
      </div>
      <div className="p-4 sm:p-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-extrabold text-navy text-lg">{design.name}</div>
          <div className="text-[12.5px] text-muted-foreground">
            {design.size_label} • {design.area_sqft} {t(lang, "sqft")} • {design.bhk} BHK
          </div>
        </div>
        <button
          onClick={onBuy}
          data-testid="bz-3d-buy"
          className="min-h-[48px] px-5 rounded-xl bg-gold text-navy font-extrabold text-[14px] flex items-center gap-2 hover:bg-gold-dark hover:text-white"
        >
          <Download className="h-4 w-4" />
          {formatINR(applyDiscount(design.price, settings))} • {t(lang, "store_buy_cta")}
        </button>
      </div>
    </div>
  </Modal>
);

const ABS_URL = (rel) =>
  rel && rel.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${rel}` : rel;

const PlanPreview = ({ design, lang, isAdmin, owned, settings, onClose, onBuy }) => {
  const planFiles =
    design.plan_files && design.plan_files.length > 0
      ? design.plan_files
      : [{ label: "Plan", url: design.plan_url }];
  const [activeIdx, setActiveIdx] = useState(0);
  const active = planFiles[activeIdx] || planFiles[0];
  const isPdf = /\.pdf(\?|$)/i.test(active.url);
  // Admins always see uploaded drawing files. Customers see them only
  // after their payment has been verified (i.e. they "own" the design).
  const canDownloadDrawings = (isAdmin || owned) && (design.floor_plan_pdf || design.technical_details_img);

  return (
    <Modal onClose={onClose} testId="bz-2d-preview">
      <div className="rounded-3xl bg-white border-2 border-navy overflow-hidden shadow-2xl">
        {planFiles.length > 1 && (
          <div className="px-4 sm:px-5 pt-3 flex gap-2 overflow-x-auto no-scrollbar bg-white border-b border-border" data-testid="bz-2d-tabs">
            {planFiles.map((f, i) => (
              <button
                key={f.label + i}
                onClick={() => setActiveIdx(i)}
                data-testid={`bz-2d-tab-${i}`}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-bold border-b-2 transition-colors ${
                  i === activeIdx
                    ? "border-gold text-navy"
                    : "border-transparent text-muted-foreground hover:text-navy"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        <div className="relative bg-sand-alt">
          {isPdf ? (
            <div className="aspect-[4/3] grid place-items-center text-navy/70 select-none">
              <div className="text-center px-6">
                <Eye className="h-10 w-10 mx-auto" />
                <div className="mt-2 font-bold">{t(lang, "bz_2d_pdf_t")}</div>
                <div className="text-sm mt-1">{t(lang, "bz_2d_pdf_d")}</div>
              </div>
            </div>
          ) : (
            <img
              src={active.url}
              alt={`${design.name} ${active.label} preview`}
              className={`w-full max-h-[60vh] object-contain bg-white ${
                isAdmin ? "" : "blur-[2px] opacity-90 select-none pointer-events-none"
              }`}
            />
          )}
          {/* Repeating watermark — hidden in admin review mode */}
          {!isAdmin && (
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none flex items-center justify-center"
              style={{
                background:
                  "repeating-linear-gradient(-30deg, transparent 0 90px, rgba(11,37,69,0.07) 90px 91px)",
              }}
            >
              <div className="text-navy/30 text-3xl sm:text-5xl font-extrabold tracking-[0.3em] uppercase rotate-[-18deg] whitespace-nowrap">
                MERA NAKSHA · PREVIEW
              </div>
            </div>
          )}
          <button
            onClick={onClose}
            data-testid="bz-2d-close"
            className="absolute top-3 right-3 h-10 w-10 grid place-items-center rounded-full bg-white/90 hover:bg-white text-navy"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-navy text-gold text-[11px] font-extrabold uppercase tracking-wider shadow-md">
            {isAdmin
              ? `${active.label} · Admin Review`
              : planFiles.length > 1
              ? `${active.label} · 2D Preview`
              : "2D Plan Preview"}
          </div>
        </div>
        {/* Specification strip + Tentative Budget/EMI estimator. Shown to
            guests and pre-unlock customers only — admins see source PDFs,
            owners see download CTAs directly. */}
        {!isAdmin && !owned && (
          <div
            className="px-4 sm:px-5 pt-4 space-y-3 border-t border-border"
            data-testid={`bz-2d-spec-strip-${design.id}`}
          >
            <div className="flex items-center gap-x-3 gap-y-1.5 flex-wrap text-[12.5px] text-muted-foreground">
              <span className="inline-flex items-center gap-1 font-bold text-navy">
                <BedDouble className="h-3.5 w-3.5" /> {design.bhk} {t(lang, "card_bedrooms")}
              </span>
              <span>·</span>
              <span className="inline-flex items-center gap-1">
                <Ruler className="h-3.5 w-3.5" /> {design.size_label}
              </span>
              {(design.num_floors || 1) > 1 && (
                <>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1">
                    <Box className="h-3.5 w-3.5" />
                    {design.num_floors}{" "}
                    {lang === "hi" ? "मंज़िलें" : "floors"}
                  </span>
                </>
              )}
              {design.facing && design.facing !== "any" && (
                <>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1 capitalize">
                    <Compass className="h-3.5 w-3.5" /> {t(lang, `facing_${design.facing}`)}
                  </span>
                </>
              )}
            </div>
            <BudgetEstimateCard
              area={design.area_sqft}
              floors={design.num_floors || 1}
            />
          </div>
        )}

        <div className="p-4 sm:p-5 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-extrabold text-navy text-lg">{design.name}</div>
            <div className="text-[12.5px] text-muted-foreground">
              {isAdmin
                ? `${planFiles.length} plan file${planFiles.length > 1 ? "s" : ""} · admin access`
                : `${t(lang, "pkg_name")} · ${planFiles.length} plan file${planFiles.length > 1 ? "s" : ""}`}
            </div>
          </div>
          {isAdmin ? (
            <a
              href={active.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="bz-2d-admin-open"
              className="min-h-[48px] px-5 rounded-xl bg-gold text-navy font-extrabold text-[14px] flex items-center gap-2 hover:bg-gold-dark hover:text-white"
            >
              <Download className="h-4 w-4" />
              Open original PDF
            </a>
          ) : owned ? (
            <a
              href={ABS_URL(design.floor_plan_pdf) || active.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`bz-2d-owned-download-${design.id}`}
              className="min-h-[48px] px-5 rounded-xl bg-emerald-600 text-white font-extrabold text-[14px] flex items-center gap-2 hover:bg-emerald-700"
            >
              <Download className="h-4 w-4" />
              {lang === "hi" ? "फ्लोर प्लान डाउनलोड करें" : "Download Floor Plan"}
            </a>
          ) : (
            <button
              onClick={onBuy}
              data-testid="bz-2d-buy"
              className="min-h-[48px] px-5 rounded-xl bg-navy text-gold font-extrabold text-[14px] flex items-center gap-2 hover:bg-navy-deep"
            >
              <Download className="h-4 w-4" />
              {formatINR(applyDiscount(design.price, settings))} • {t(lang, "bz_unlock_full")}
            </button>
          )}
        </div>

        {/* Admin-uploaded drawing files (floor-plan PDF + technical details
            image). Shown to admins and to customers who own the design. */}
        {canDownloadDrawings && (
          <div
            data-testid={`bz-2d-drawing-files-${design.id}`}
            className="px-4 sm:px-5 pb-4 border-t border-border pt-3 grid grid-cols-1 sm:grid-cols-2 gap-2"
          >
            {design.floor_plan_pdf && (
              <a
                href={ABS_URL(design.floor_plan_pdf)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`bz-2d-floor-plan-${design.id}`}
                className="min-h-[44px] rounded-xl bg-navy text-gold font-extrabold text-[13px] inline-flex items-center justify-center gap-2 hover:bg-navy-deep"
              >
                <Download className="h-3.5 w-3.5" />
                {lang === "hi" ? "Floor Plan PDF" : "View Floor Plan PDF"}
              </a>
            )}
            {design.technical_details_img && (
              <a
                href={ABS_URL(design.technical_details_img)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`bz-2d-technical-${design.id}`}
                className="min-h-[44px] rounded-xl bg-white text-navy border-2 border-navy font-extrabold text-[13px] inline-flex items-center justify-center gap-2 hover:bg-sand-alt"
              >
                <Eye className="h-3.5 w-3.5" />
                {lang === "hi" ? "Technical Details" : "View Technical Details"}
              </a>
            )}
          </div>
        )}

        {/* Phase-1 scope (what's included vs not included) + customization
            add-ons. Hidden in admin-review mode since admins are just
            inspecting the source PDFs. */}
        {!isAdmin && (
          <div className="px-4 sm:px-5 pb-5 space-y-4 border-t border-border pt-4">
            <div className="text-[11.5px] uppercase tracking-[0.18em] text-navy/70 font-extrabold">
              {t(lang, "pkg_name")}
            </div>
            <Phase1Scope lang={lang} testId={`phase1-scope-${design.id}`} />
            <AddonsSection
              lang={lang}
              designName={design.name}
              testId={`addons-section-${design.id}`}
            />
            <LegalDisclaimer
              lang={lang}
              variant="block"
              testId={`preview-legal-${design.id}`}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

// (Removed external api.qrserver.com helper — we now render the QR client-side
// with qrcode.react for reliability and offline-friendliness.)

// Lazy-load the Razorpay Checkout script (idempotent)

export default NakshaStore;
